/**
 * notificationCapability — what THIS device can do about notifications, and
 * how to fix it when it can't (platform-aware permission, 2026-09-08).
 *
 * Pure: reads the environment through one plain record so every
 * platform × permission cell is unit-testable without a browser. The one
 * side-effecting export, `requestPushPermission`, is the click path — it
 * asks the browser and then hands off to PushRegistration.
 *
 * WHY THIS EXISTS. Before this the app asked for permission on every mount
 * with no gesture (browsers refuse that), ignored the answer, had no denied
 * state, and drew `new Notification()` from the tab — which throws on Android
 * Chrome and needs an installed app on iOS. The state machine below is the
 * single truth every surface renders from (the Settings card, the mobile
 * menu card, the chat header bell tooltip):
 *
 *   'unsupported'      — no Notification / service worker / PushManager here;
 *   'needs-install'    — iOS Safari in a tab (web push exists only inside an
 *                        installed app, and only from iOS 16.4);
 *   'default'          — never asked: ONE primary button, asked inside a click;
 *   'denied'           — blocked in the browser/OS: per-platform recovery
 *                        steps + "check again"; the button is never disabled;
 *   'granted-no-token' — allowed, but no push token yet for this account
 *                        (registration pending or failed — a retry is offered);
 *   'granted'          — on for this device.
 */

export type NotificationPlatform =
  | 'ios-safari'
  | 'android-chrome'
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'safari-mac'
  | 'other';

export type NotificationState =
  | 'unsupported'
  | 'needs-install'
  | 'default'
  | 'denied'
  | 'granted-no-token'
  | 'granted';

export type NotificationPermissionValue = 'default' | 'denied' | 'granted';

/** Everything the decision reads — a plain record so tests can build any cell. */
export interface NotificationEnvironment {
  ua: string;
  /** Installed-app display (display-mode: standalone, or iOS navigator.standalone). */
  standalone: boolean;
  hasNotification: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  /** null when the Notification API is absent. */
  permission: NotificationPermissionValue | null;
  /** iPadOS reports a Macintosh UA; touch points tell it apart. */
  maxTouchPoints: number;
}

/** Web push arrived in iOS 16.4 — and only for a Home Screen app. */
export const IOS_WEB_PUSH_MIN_VERSION = 16.4;

/**
 * The messaging service worker's registration (PushRegistration owns the
 * register call). Firebase's conventional scope keeps the worker off the
 * page's own scope — it never controls navigations, only push.
 */
export const MESSAGING_SW_PATH = '/firebase-messaging-sw.js';
export const MESSAGING_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/** iOS major.minor from a Safari/WebKit UA ("OS 16_4" → 16.4); null when absent. */
export function parseIosVersion(ua: string): number | null {
  const m = /OS (\d+)[_.](\d+)/.exec(ua || '');
  if (!m) return null;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  if (!Number.isFinite(major)) return null;
  return Number(`${major}.${Number.isFinite(minor) ? minor : 0}`);
}

function isIosLike(env: Pick<NotificationEnvironment, 'ua' | 'maxTouchPoints'>): boolean {
  const ua = env.ua || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS 13+ masquerades as a Mac; a Mac has no touch points.
  return /Macintosh/i.test(ua) && (env.maxTouchPoints || 0) > 1;
}

export function detectNotificationPlatform(
  env: Pick<NotificationEnvironment, 'ua' | 'maxTouchPoints'>,
): NotificationPlatform {
  const ua = env.ua || '';
  if (isIosLike(env)) return 'ios-safari';
  if (/Android/i.test(ua)) return 'android-chrome';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Chrome\/|CriOS\/|Chromium\//i.test(ua)) return 'chrome';
  if (/Safari\//i.test(ua) && /Macintosh/i.test(ua)) return 'safari-mac';
  return 'other';
}

/** Snapshot the live browser into the plain record (SSR/jsdom-safe). */
export function readNotificationEnvironment(): NotificationEnvironment {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const win = typeof window !== 'undefined' ? window : null;
  let standalone = false;
  try {
    standalone = Boolean(
      (nav as unknown as { standalone?: boolean } | null)?.standalone === true
      || (win?.matchMedia && win.matchMedia('(display-mode: standalone)').matches),
    );
  } catch { /* matchMedia unavailable */ }
  const hasNotification = typeof Notification !== 'undefined';
  let permission: NotificationPermissionValue | null = null;
  if (hasNotification) {
    const raw = Notification.permission;
    permission = raw === 'granted' || raw === 'denied' ? raw : 'default';
  }
  return {
    ua: nav?.userAgent || '',
    standalone,
    hasNotification,
    hasServiceWorker: Boolean(nav && 'serviceWorker' in nav),
    hasPushManager: typeof win !== 'undefined' && win !== null && 'PushManager' in win,
    permission,
    maxTouchPoints: nav?.maxTouchPoints || 0,
  };
}

/**
 * The state machine. `hasToken` is whether PushRegistration holds a live push
 * token for the signed-in account on this device — the difference between
 * 'granted' and 'granted-no-token'.
 */
export function getNotificationState(
  env: NotificationEnvironment,
  opts: { hasToken: boolean },
): NotificationState {
  const platform = detectNotificationPlatform(env);
  if (platform === 'ios-safari') {
    const version = parseIosVersion(env.ua);
    if (version !== null && version < IOS_WEB_PUSH_MIN_VERSION) return 'needs-install';
    if (!env.standalone) return 'needs-install';
  }
  if (!env.hasNotification || !env.hasServiceWorker || !env.hasPushManager) return 'unsupported';
  if (env.permission === 'denied') return 'denied';
  if (env.permission === 'granted') return opts.hasToken ? 'granted' : 'granted-no-token';
  return 'default';
}

/**
 * How to un-block notifications, per platform. UI chrome copy — lowercase
 * house voice, plain words, the place names the OS actually uses.
 */
export function recoverySteps(platform: NotificationPlatform): string[] {
  switch (platform) {
    case 'ios-safari':
      return [
        'open the settings app on your iphone or ipad',
        'tap notifications, then find prest0 in the list',
        'turn on allow notifications',
        'come back here and tap "check again"',
      ];
    case 'android-chrome':
      return [
        'tap the lock icon (or ⓘ) next to the address bar',
        'open permissions → notifications and set it to allow',
        'if it is not there: android settings → apps → chrome → notifications → prest0.ai',
        'come back here and tap "check again"',
      ];
    case 'chrome':
    case 'edge':
      return [
        'click the lock icon (or the tune icon) left of the address bar',
        'set notifications to allow',
        'reload the page, then tap "check again"',
      ];
    case 'firefox':
      return [
        'click the permissions icon left of the address bar',
        'remove the "blocked" entry next to notifications',
        'reload the page, then tap "check again"',
      ];
    case 'safari-mac':
      return [
        'open safari → settings → websites',
        'choose notifications in the left column',
        'set prest0.ai to allow',
        'come back here and tap "check again"',
      ];
    default:
      return [
        'open your browser\'s site settings for prest0.ai',
        'allow notifications',
        'come back here and tap "check again"',
      ];
  }
}

/** One honest line per state, for tooltips and the card's summary. */
export function describeNotificationState(state: NotificationState, platform: NotificationPlatform): string {
  switch (state) {
    case 'unsupported':
      return 'this browser can\'t show notifications from prest0 — in-app chimes still work';
    case 'needs-install':
      return platform === 'ios-safari'
        ? 'on iphone and ipad, notifications only work from the installed app (ios 16.4 or later) — add prest0 to your home screen first'
        : 'install the app to get notifications on this device';
    case 'default':
      return 'notifications are not on yet for this device';
    case 'denied':
      return 'notifications are blocked for prest0 in your browser or device settings';
    case 'granted-no-token':
      return 'notifications are allowed, but this device isn\'t registered yet';
    case 'granted':
    default:
      return 'notifications are on for this device';
  }
}

/**
 * The CLICK path — the only place the permission prompt is ever requested.
 * Never called from a mount or a timer: browsers refuse a prompt without a
 * user gesture, and asking anyway burns the one chance to ask. Returns the
 * state AFTER the attempt so the caller can render it straight away.
 */
export async function requestPushPermission(): Promise<NotificationState> {
  const env = readNotificationEnvironment();
  const before = getNotificationState(env, { hasToken: false });
  if (before === 'unsupported' || before === 'needs-install') return before;

  let permission: NotificationPermissionValue | null = env.permission;
  if (permission === 'default') {
    try {
      const answer = await Notification.requestPermission();
      permission = answer === 'granted' || answer === 'denied' ? answer : 'default';
    } catch {
      permission = env.permission;
    }
  }
  if (permission !== 'granted') {
    return getNotificationState({ ...env, permission }, { hasToken: false });
  }
  // Dynamic import keeps this module pure for its own tests; the registration
  // module carries the Firebase dependencies.
  let token: string | null = null;
  try {
    const { registerPush } = await import('./PushRegistration');
    token = await registerPush();
  } catch {
    token = null;
  }
  return getNotificationState({ ...env, permission: 'granted' }, { hasToken: Boolean(token) });
}
